/**
 * Events & Signups API client. Thin wrappers over the shared `apiFetch` helper
 * (see ./api) that map to the Elysia /events module.
 */
import type {
  EventDto,
  EventOccurrenceDto,
  NewEventInput,
  NewOccurrenceInput,
  NewSignupInput,
  Paginated,
  SignupDto,
  SignupResult,
  UpdateEventInput,
  UpdateOccurrenceInput,
  UpdateSignupInput,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  EventDto,
  EventOccurrenceDto,
  SignupDto,
  SignupResult,
  NewEventInput,
  NewOccurrenceInput,
  NewSignupInput,
} from '@fc/core'

// ---- Events ----
export const listEvents = async (): Promise<EventDto[]> => {
  const data = await apiFetch<Paginated<EventDto>>('/events')
  return data.items
}

export const getEvent = (id: string) => apiFetch<EventDto>(`/events/${id}`)

export const createEvent = (input: NewEventInput) =>
  apiFetch<EventDto>('/events', { method: 'POST', body: JSON.stringify(input) })

export const updateEvent = (id: string, input: UpdateEventInput) =>
  apiFetch<EventDto>(`/events/${id}`, { method: 'PATCH', body: JSON.stringify(input) })

export const deleteEvent = (id: string) => apiFetch<void>(`/events/${id}`, { method: 'DELETE' })

// ---- Occurrences ----
export const listOccurrences = (eventId: string) =>
  apiFetch<EventOccurrenceDto[]>(`/events/${eventId}/occurrences`)

export const createOccurrence = (eventId: string, input: NewOccurrenceInput) =>
  apiFetch<EventOccurrenceDto>(`/events/${eventId}/occurrences`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const updateOccurrence = (
  eventId: string,
  occurrenceId: string,
  input: UpdateOccurrenceInput,
) =>
  apiFetch<EventOccurrenceDto>(`/events/${eventId}/occurrences/${occurrenceId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const deleteOccurrence = (eventId: string, occurrenceId: string) =>
  apiFetch<void>(`/events/${eventId}/occurrences/${occurrenceId}`, { method: 'DELETE' })

// ---- Signups ----
export const listSignups = (eventId: string, occurrenceId?: string) =>
  apiFetch<SignupDto[]>(
    `/events/${eventId}/signups${occurrenceId ? `?occurrenceId=${occurrenceId}` : ''}`,
  )

export const createSignup = (eventId: string, input: NewSignupInput) =>
  apiFetch<SignupResult>(`/events/${eventId}/signups`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const updateSignup = (eventId: string, signupId: string, input: UpdateSignupInput) =>
  apiFetch<SignupDto>(`/events/${eventId}/signups/${signupId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const deleteSignup = (eventId: string, signupId: string) =>
  apiFetch<void>(`/events/${eventId}/signups/${signupId}`, { method: 'DELETE' })
